import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  Icon,
  Spacer,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { NotebookPen, Plus } from 'lucide-react';
import { djs } from '@duke-hq/djs';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { trpcAdmin } from '../../lib/trpcAdmin';
import { testIds } from '../testIds';

type EmployerNote = RouterOutputs['workPlacements']['employers']['listEmployerNotes'][number];

const NOTE_CARD_MAX_WIDTH = pxToRem(420);

function NoteCard({ note }: { note: EmployerNote }): React.ReactElement {
  return (
    <Flex justify="flex-end">
      <Box
        data-testid={testIds.employerNoteItem}
        w="100%"
        maxW={NOTE_CARD_MAX_WIDTH}
        bg="bg.surface"
        borderRadius="lg"
        boxShadow="sm"
        p={4}
      >
        <Stack gap={3}>
          <HStack gap={2} fontSize="sm" fontWeight="medium">
            <Icon as={NotebookPen} boxSize={iconSize.md} color="fg.muted" />
            <Text>
              <Trans>General</Trans>
            </Text>
          </HStack>
          <Divider borderColor="bg.muted" />
          <Text
            fontSize="sm"
            color="fg.muted"
            lineHeight="tall"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {note.text}
          </Text>
          <Divider borderColor="bg.muted" />
          <HStack gap={2}>
            <Avatar size="xs" name={note.author.name} src={undefined} />
            <Text fontSize="xs" fontWeight="medium">
              {note.author.name}
            </Text>
            <Spacer />
            <Text fontSize="xs" color="fg.subtle" whiteSpace="nowrap">
              {djs(note.createdAt).format('LLL')}
            </Text>
          </HStack>
        </Stack>
      </Box>
    </Flex>
  );
}

export function EmployersNotesTab({ employerId }: { employerId: string }): React.ReactElement {
  const [text, setText] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const {
    data: notes,
    isLoading,
    isError,
  } = trpcAdmin.workPlacements.employers.listEmployerNotes.useQuery({ employerId });

  const addNote = trpcAdmin.workPlacements.employers.addEmployerNote.useMutation({
    onSuccess: () => {
      setText('');
      setIsComposing(false);
      void utils.workPlacements.employers.listEmployerNotes.invalidate({ employerId });
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to add note`, description: error.message });
    },
  });

  const trimmed = text.trim();

  function closeComposer(): void {
    setIsComposing(false);
    setText('');
  }

  const trigger = (
    <Button
      size="sm"
      data-testid={testIds.addNoteTrigger}
      leftIcon={<Icon as={Plus} boxSize={iconSize.sm} />}
      onClick={() => setIsComposing(true)}
    >
      <Trans>Add note</Trans>
    </Button>
  );

  const composer = (
    <Stack gap={2}>
      <Textarea
        data-testid={testIds.noteTextInput}
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={t`Add a note about this employer`}
        rows={3}
        bg="bg.surface"
      />
      <HStack justify="flex-end" gap={2}>
        <Button
          variant="ghost"
          size="sm"
          data-testid={testIds.cancelNoteButton}
          onClick={closeComposer}
        >
          <Trans>Cancel</Trans>
        </Button>
        <Button
          size="sm"
          data-testid={testIds.addNoteButton}
          isDisabled={trimmed === '' || addNote.isPending}
          isLoading={addNote.isPending}
          onClick={() => addNote.mutate({ employerId, text: trimmed })}
        >
          <Trans>Add note</Trans>
        </Button>
      </HStack>
    </Stack>
  );

  if (isLoading) {
    return (
      <Center p={8} bg="bg.subtle" minH="100%">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Center p={8} bg="bg.subtle" minH="100%">
        <Text data-testid={testIds.notesErrorState} color="fg.error" fontSize="sm">
          <Trans>Could not load notes. Please try again.</Trans>
        </Text>
      </Center>
    );
  }

  if (notes == null || notes.length === 0) {
    return (
      <Stack gap={8} p={4} bg="bg.subtle" minH="100%" data-testid={testIds.notesEmptyState}>
        {isComposing ? composer : <Center>{trigger}</Center>}
        <Text color="fg.muted" fontSize="sm" textAlign="center">
          <Trans>No notes available</Trans>
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap={4} p={4} bg="bg.subtle" minH="100%">
      {isComposing ? composer : <Center>{trigger}</Center>}
      <Stack gap={4} data-testid={testIds.employerNotesList}>
        {notes.map((note) => (
          <NoteCard key={note.id} note={note} />
        ))}
      </Stack>
    </Stack>
  );
}
